import React from 'react';
import { Box } from '@mui/material';

import { Page, Table } from 'components';
import { capitalizeFirstLetter, getNumberOfPages } from 'core/shared';
import { useNavigate } from 'hooks/useNavigate';
import { useSearchParams } from 'hooks/useSearchParams';
import { useTranslations } from 'hooks/useTranslations';

import { UseQuery } from 'useQuery';

interface TransactionRuleGroupRow {
  id: string;
  name: string;
  description: string;
  type: string;
  codes: string;
}

function TransactionRuleGroupsPage() {
  const translations = useTranslations('transactionRuleGroupsPage');
  const { navigate } = useNavigate();

  const { data: transactionRuleGroups, isLoading: loadingTransactionRuleGroups } =
    UseQuery.transactionRuleGroups({ page: 1, limit: 10000 });
  const { searchParams, setSearchParams } = useSearchParams('transactionRuleGroups');

  const [page, setPage] = React.useState(1);
  const [parsedRows, setParsedRows] = React.useState<TransactionRuleGroupRow[]>([]);

  React.useEffect(() => {
    if (searchParams.page) {
      setPage(searchParams.page);
    }
  }, [searchParams]);

  React.useEffect(() => {
    const parsedGroups: TransactionRuleGroupRow[] = (transactionRuleGroups?.result ?? []).map(
      (group) => ({
        id: group.id,
        name: group.name,
        description: group.description,
        type: capitalizeFirstLetter(group.type.toLowerCase().replaceAll('_', ' ')),
        codes: `${(group.rule?.codes?.length ?? 0).toString()}`,
      }),
    );

    setParsedRows(parsedGroups);
  }, [transactionRuleGroups]);

  return (
    <Page
      name="transactionRuleGroups"
      actions={[
        {
          svgName: 'add',
          title: translations.add,
          onClick: () => {
            navigate({
              name: 'transactionRuleGroup',
              id: 'new',
              searchParams: undefined,
              metaKey: false,
            });
          },
          mode: 'closeOnClick',
        },
      ]}
    >
      <Box>
        <Table
          columns={{
            name: { name: translations.name, type: 'text', flex: 1 },
            description: { name: translations.description, type: 'text', flex: 1 },
            type: { name: translations.type, type: 'text', flex: 1 },
            codes: { name: translations.amountOfCodes, type: 'text', flex: 1 },
          }}
          rows={parsedRows}
          selectable={false}
          loading={loadingTransactionRuleGroups}
          onClickRow={({ id, metaKey }) => {
            navigate({
              name: 'transactionRuleGroup',
              id: id.toString(),
              searchParams: undefined,
              metaKey,
            });
          }}
          pagination={{
            count: getNumberOfPages({ meta: transactionRuleGroups?.meta }),
            page,
            onChange: (newPage) => {
              setSearchParams({
                page: newPage,
              });
              setPage(newPage);
            },
          }}
        />
      </Box>
    </Page>
  );
}

export { TransactionRuleGroupsPage };
